import { load as yamlLoad, YAMLException } from "js-yaml";

/** Read a selected plugin folder into a manifest object + component files.
 *  Strips the top-level folder segment browsers prepend to webkitRelativePath,
 *  parses plugin.yaml (YAML or JSON) into an object, and collects every other
 *  file (excluding plugin.yaml, which packageAndSign injects). */
export async function readPluginFolder(
  files: File[],
): Promise<{ manifest: Record<string, unknown>; files: { path: string; bytes: Uint8Array }[] }> {
  const rel = (f: File): string => {
    const p = f.webkitRelativePath || f.name;
    const slash = p.indexOf("/");
    return slash >= 0 ? p.slice(slash + 1) : p; // strip top folder segment
  };

  const manifestFile = files.find((f) => rel(f) === "plugin.yaml");
  if (!manifestFile) {
    throw new Error("folder must contain plugin.yaml at its root");
  }

  const manifestText = new TextDecoder().decode(
    new Uint8Array(await manifestFile.arrayBuffer()),
  );
  let parsed: unknown;
  try {
    parsed = yamlLoad(manifestText);
  } catch (e) {
    const msg = e instanceof YAMLException ? e.message : String(e);
    throw new Error(`plugin.yaml is not valid YAML: ${msg}`);
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("plugin.yaml must be a mapping");
  }
  const manifest: Record<string, unknown> = parsed as Record<string, unknown>;

  const out: { path: string; bytes: Uint8Array }[] = [];
  for (const f of files) {
    const path = rel(f);
    if (path === "plugin.yaml") continue;
    out.push({ path, bytes: new Uint8Array(await f.arrayBuffer()) });
  }
  return { manifest, files: out };
}
